import React, { useState } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { DeleteRegular, EditRegular, ReOrderDotsHorizontalRegular } from "@fluentui/react-icons";
import { useRestoreFocusTarget, mergeClasses, Body1, Tooltip, Button } from "@fluentui/react-components";

import { Stack } from "@/components/stack";

import { DeletePlaybookRuleDialog } from "../delete-playbook-rule-dialog";
import { usePlaybookEditorStyles } from "../../styles";

import { ClauseData } from "@/types/risk-analysis";

export interface PlaybookRuleRowProps extends React.ComponentPropsWithoutRef<"li"> {
  disabled?: boolean;
  playbookRule: ClauseData;
  onEdit: () => void;
  onDelete: () => void;
}

export function PlaybookRuleRow({
  id,
  disabled,
  playbookRule,
  className,
  style,
  onEdit,
  onDelete,
  ...props
}: PlaybookRuleRowProps) {
  const styles = usePlaybookEditorStyles();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, active } = useSortable({ id });

  const restoreDeleteTargetAttribute = useRestoreFocusTarget();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    onDelete();
  };

  return (
    <li
      id={id}
      ref={setNodeRef}
      className={mergeClasses(styles.playbookRuleRow, className)}
      style={{
        ...style,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...props}
    >
      <Stack align="center" justify="between" gap="1rem">
        <Stack align="center" gap="0.5rem">
          <span
            ref={setActivatorNodeRef}
            style={{ display: "inline-flex", cursor: active ? "grabbing" : "grab" }}
            {...listeners}
          >
            <ReOrderDotsHorizontalRegular />
          </span>
          <Body1>{playbookRule.clauseTopic}</Body1>
        </Stack>
        <Stack align="center" gap="0.5rem">
          <Tooltip relationship="label" content="Delete playbook rule">
            <Button
              disabled={disabled}
              appearance="primary"
              icon={<DeleteRegular />}
              aria-label="Delete playbook rule"
              onClick={() => setDeleteDialogOpen(true)}
              {...restoreDeleteTargetAttribute}
            />
          </Tooltip>
          <Tooltip relationship="label" content="Edit playbook rule">
            <Button
              disabled={disabled}
              appearance="primary"
              icon={<EditRegular />}
              aria-label="Edit playbook rule"
              onClick={onEdit}
            />
          </Tooltip>
        </Stack>
      </Stack>
      <DeletePlaybookRuleDialog
        playbookRule={playbookRule}
        open={deleteDialogOpen}
        onAccept={handleDelete}
        onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
      />
    </li>
  );
}
